import type { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { color, diagramStyle } from './palette';

/**
 * Many sources → one shape → corroborated fact: four differently shaped sources mapped into Lantern's one shape, and
 * the same fact read across them, corroborated where they agree and an exception where one does not.
 */
export const SourcesToOneShape: FC = () => {
  const theme = useTheme();
  const sub = { fill: color.muted, fontSize: 13 } as const;
  const lbl = { fontSize: 15, fontWeight: 600 } as const;
  const row = { fontSize: 14, fill: color.fg } as const;
  const sources: Array<[number, string]> = [
    [53, 'Fund administrator'],
    [123, 'Portfolio monitoring'],
    [193, "The firm's spreadsheet"],
    [263, 'LP portal'],
  ];

  return (
    <svg viewBox="0 0 940 320" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Many sources → one shape → corroborated fact" style={diagramStyle(theme)}>
      <defs>
        <marker id="academy-arrow-sources" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill={color.muted} />
        </marker>
      </defs>
      {/* Four sources, each its own shape */}
      <rect x="20" y="30" width="220" height="46" rx="6" fill="none" stroke={color.muted} strokeWidth="2" />
      <rect x="20" y="100" width="220" height="46" rx="23" fill="none" stroke={color.muted} strokeWidth="2" />
      <g stroke={color.muted} strokeWidth="2">
        <rect x="20" y="170" width="220" height="46" rx="2" fill="none" />
        <line x1="20" y1="181" x2="240" y2="181" opacity=".3" strokeWidth="1" />
        <line x1="75" y1="170" x2="75" y2="181" opacity=".3" strokeWidth="1" />
        <line x1="185" y1="170" x2="185" y2="181" opacity=".3" strokeWidth="1" />
      </g>
      <polygon points="40,263 60,240 200,240 220,263 200,286 60,286" fill="none" stroke={color.muted} strokeWidth="2" />
      {sources.map(([cy, name], i) => (
        <g key={name}>
          <text x="130" y={cy + 5} textAnchor="middle" style={row}>
            {name}
          </text>
          <line x1={i === 3 ? 222 : 242} y1={cy} x2="366" y2={125 + i * 20} stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-sources)" />
        </g>
      ))}
      {/* Lantern: one shape */}
      <rect x="370" y="85" width="220" height="140" rx="12" fill="none" stroke={color.accent} strokeWidth="2.5" />
      <text x="480" y="118" textAnchor="middle" style={lbl} fill={color.accent}>
        Lantern
      </text>
      <text x="480" y="140" textAnchor="middle" style={sub}>
        one shape
      </text>
      <rect x="390" y="156" width="180" height="22" rx="3" fill={color.accent} opacity=".12" />
      <text x="480" y="171" textAnchor="middle" style={sub}>
        fund · fact · as-of · value
      </text>
      <rect x="390" y="184" width="180" height="22" rx="3" fill={color.accent} opacity=".18" />
      <text x="480" y="199" textAnchor="middle" style={row}>
        Fund III · NAV · 30 Jun
      </text>
      <line x1="592" y1="155" x2="646" y2="155" stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-sources)" />
      {/* One fact, read across the sources */}
      <rect x="650" y="30" width="270" height="260" rx="12" fill="none" stroke={color.muted} strokeWidth="2" />
      <text x="785" y="62" textAnchor="middle" style={lbl} fill={color.fg}>
        Fund III · NAV · 30 Jun
      </text>
      <line x1="670" y1="78" x2="900" y2="78" stroke={color.muted} strokeWidth="1" opacity=".4" />
      {[
        ['Fund administrator', '412.0m'],
        ['Portfolio monitoring', '412.0m'],
        ['LP portal', '412.0m'],
      ].map(([name, value], i) => (
        <g key={name}>
          <text x="670" y={106 + i * 30} style={row}>
            {name}
          </text>
          <text x="872" y={106 + i * 30} textAnchor="end" style={row}>
            {value}
          </text>
          <text x="898" y={107 + i * 30} textAnchor="middle" fontSize="15" fontWeight="700" fill={color.ok}>
            ✓
          </text>
        </g>
      ))}
      <text x="670" y="196" style={row}>
        Spreadsheet
      </text>
      <text x="872" y="196" textAnchor="end" fontSize="14" fontWeight="600" fill={color.warn}>
        398.0m
      </text>
      <text x="898" y="197" textAnchor="middle" fontSize="15" fontWeight="700" fill={color.warn}>
        !
      </text>
      <rect x="670" y="216" width="230" height="28" rx="14" fill={color.ok} opacity=".18" />
      <text x="785" y="235" textAnchor="middle" fontSize="14" fontWeight="600" fill={color.ok}>
        ✓ corroborated · 3 of 4 agree
      </text>
      <text x="785" y="272" textAnchor="middle" fontSize="13" fill={color.warn}>
        spreadsheet → exception raised
      </text>
    </svg>
  );
};
